import flowChartApp from 'common/builder/components/chart/FlowChart/app'
import * as atypes from 'common/builder/constants/builderReduxActionTypes'
import * as snapshotConstants from 'common/builder/constants/Snapshots'
import * as builderStateSelectors from 'common/builder/selectors/builder/builderStateSelectors'
import * as entitySelectors from 'common/builder/selectors/builder/entitySelectors'
import * as snapshotSelectors from 'common/builder/selectors/builder/snapshotSelectors'
import { IThunkAction } from 'common/core/interfaces/actions'

export const generateBuilderSnapshot = (prevState: RootState, builderId: string): IThunkAction => {
  return (dispatch, getState) => {
    const builderState = builderStateSelectors.getById(prevState, builderId)
    const builderNodes = entitySelectors.getNodesMap(prevState, builderId)
    const builderBlocks = entitySelectors.getBlocksMap(prevState, builderId)
    const builderButtons = entitySelectors.getButtonsMap(prevState, builderId)

    const snapshot = {
      state: builderState,
      nodes: { byId: builderNodes },
      blocks: { byId: builderBlocks },
      buttons: { byId: builderButtons },
    }

    const snapshotIndex = snapshotSelectors.getSnapshotIndex(prevState, builderId)
    if (snapshotIndex) {
      dispatch({ type: atypes.REMOVE_NEWEST_BUILDER_SNAPSHOTS, builderId })
    }

    const snapshotList = snapshotSelectors.getSnapshotList(getState(), builderId)
    if (snapshotList.length >= snapshotConstants.SNAPSHOT_LIST_LIMIT) {
      dispatch({ type: atypes.REMOVE_LIMITED_BUILDER_SNAPSHOT, builderId })
    }

    return dispatch({ type: atypes.ADD_BUILDER_SNAPSHOT, snapshot, builderId })
  }
}

export const undo = (builderId: string): IThunkAction => {
  return (dispatch, getState) => {
    const prevSnapshotIndex = snapshotSelectors.getSnapshotIndex(getState(), builderId)
    const snapshotIndex = prevSnapshotIndex + 1

    const isSnapshotInProgress = snapshotSelectors.getSnapshotInstallationStatus(
      getState(),
      builderId,
    )
    let snapshotList = snapshotSelectors.getSnapshotList(getState(), builderId)
    if (!snapshotList.length || isSnapshotInProgress) return

    if (snapshotIndex === 1) {
      dispatch(generateBuilderSnapshot(getState(), builderId))
    }

    snapshotList = snapshotSelectors.getSnapshotList(getState(), builderId)
    if (snapshotIndex > snapshotList.length - 1) return

    dispatch({ type: atypes.START_INSTALL_BUILDER_SNAPSHOT, builderId })

    const targetSnapshot = snapshotSelectors.getTargetSnapshot(getState(), builderId, snapshotIndex)

    dispatch(updateBuilderCoordinate(targetSnapshot.state.coordinates))
    dispatch({
      type: atypes.INSTALL_BUILDER_SNAPSHOT,
      snapshotIndex,
      targetSnapshot,
      builderId,
    })

    dispatch({ type: atypes.STOP_INSTALL_BUILDER_SNAPSHOT, builderId })
  }
}

export const redo = (builderId: string): IThunkAction => {
  return (dispatch, getState) => {
    const prevSnapshotIndex = snapshotSelectors.getSnapshotIndex(getState(), builderId)
    const isSnapshotInProgress = snapshotSelectors.getSnapshotInstallationStatus(
      getState(),
      builderId,
    )
    const snapshotIndex = prevSnapshotIndex - 1
    if (snapshotIndex < 0 || isSnapshotInProgress) return

    dispatch({ type: atypes.START_INSTALL_BUILDER_SNAPSHOT, builderId })

    const targetSnapshot = snapshotSelectors.getTargetSnapshot(getState(), builderId, snapshotIndex)
    dispatch(updateBuilderCoordinate(targetSnapshot.state.coordinates))
    dispatch({
      type: atypes.INSTALL_BUILDER_SNAPSHOT,
      snapshotIndex,
      targetSnapshot,
      builderId,
    })

    if (snapshotIndex === 0) {
      dispatch({ type: atypes.REMOVE_LAST_GENERATED_BUILDER_SNAPSHOT, builderId })
    }

    dispatch({ type: atypes.STOP_INSTALL_BUILDER_SNAPSHOT, builderId })
  }
}

interface BuilderNode {
  parent: {
    x?: number
    y?: number
    id?: string
    updateFrame?(): void
  }
  position: {
    set(x: number, y: number, status: boolean): void
  }
}

export function updateBuilderCoordinate(coordinates: UnsafeAnyObject): IThunkAction {
  return (dispatch) => {
    let selectedGroup: BuilderNode['parent'] = {}

    Object.keys(coordinates).forEach((id) => {
      const coords = coordinates[id]

      const node: BuilderNode = flowChartApp.getNode(id)
      if (!node || !coords) {
        return
      }

      let { x, y } = coords

      const { parent } = node
      if (parent && parent.id === '$SelectedGroup$') {
        selectedGroup = parent
        x -= parent.x ?? 0
        y -= parent.y ?? 0
      }

      node.position.set(x, y, true)
    })

    selectedGroup && selectedGroup.updateFrame?.()
    dispatch({ type: atypes.INSTALL_BUILDER_SNAPSHOT_COORDINATES, coordinates })
  }
}
